import React, { useEffect, useState } from "react";

const AcceptInvitationModal = ({
  isOpen,
  onClose,
  onAccept,
  buyerName,
  itemName,
  itemSupportsDelivery = false,
  deliveryFee = null,
  defaultIncludeDeliveryFee = false,
  lockDeliveryOption = false,
}) => {
  const [includeDeliveryFee, setIncludeDeliveryFee] = useState(false);
  const [deliveryFeeError, setDeliveryFeeError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setIncludeDeliveryFee(Boolean(defaultIncludeDeliveryFee));
    setDeliveryFeeError("");
  }, [isOpen, defaultIncludeDeliveryFee]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-[12px] p-5 md:p-8 max-w-[625px] w-full mx-4">
        {/* Header */}
        <h3 className="text-page-title md:text-page-title-d font-rubik text-gray-[#1E1E1E] mb-4">
          Accept invitation
        </h3>

        {/* Content */}
        <div className="mb-8 text-page-subtitle-d font-normal opacity-50">
          <p className="text-[#1E1E1E] mb-4 leading-relaxed">
            Are you sure you want to accept{" "}
            <span className="">{buyerName}</span>'s request for{" "}
            <span className="">{itemName}</span>?
          </p>
          <p className="text-[#1E1E1E]">
            This will start the transaction process.
          </p>
        </div>

        {/* Authentication / delivery option (only if enabled on item) */}
        {itemSupportsDelivery && (
          <div className="mb-8">
            {deliveryFeeError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-[13px]">{deliveryFeeError}</p>
              </div>
            )}
            <div
              onClick={() => {
                if (lockDeliveryOption) return;
                if (!deliveryFee) {
                  setDeliveryFeeError(
                    "Authentication fee information is not available right now. Please close and reopen the modal."
                  );
                  return;
                }
                setIncludeDeliveryFee((v) => !v);
              }}
              className={`flex flex-col md:flex-row md:items-center md:justify-between p-2 rounded-lg border-2 cursor-pointer transition-all ${
                includeDeliveryFee
                  ? "bg-green-50 border-green-500"
                  : deliveryFeeError || !deliveryFee
                  ? "bg-gray-50 border-gray-300 opacity-60"
                  : "bg-white border-gray-200 hover:border-esblue"
              } ${
                lockDeliveryOption || !deliveryFee ? "cursor-not-allowed" : ""
              }`}
            >
              <div className="flex-1 flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    includeDeliveryFee
                      ? "bg-green-500 border-green-500"
                      : "border-gray-300"
                  }`}
                >
                  {includeDeliveryFee && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
                <div>
                  <p className="text-[12px] text-gray-500 mb-0">
                    Authenticate and get it delivered to your door
                  </p>
                  {deliveryFee ? (
                    <p
                      className={`text-[18px] font-[600] ${
                        includeDeliveryFee ? "text-green-600" : "text-[#1E1E1E]"
                      }`}
                    >
                      {Number(deliveryFee).toLocaleString()}€
                    </p>
                  ) : (
                    <p className="text-[18px] font-[600] text-red-600">
                      Not available
                    </p>
                  )}
                </div>
              </div>
              <div className="mt-2 md:mt-0 md:ml-4">
                {includeDeliveryFee ? (
                  <span className="text-green-600 font-[600] text-[14px]">
                    Added
                  </span>
                ) : (
                  <span className="text-esblue font-[600] text-[14px] flex items-center gap-1">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    Add
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-3 order-2 md:order-1 text-esblue min-w-[147px] rounded-[8px] bg-[#F5F8FF]"
          >
            Cancel
          </button>
          <button
            onClick={() => onAccept?.(includeDeliveryFee)}
            className="px-4 py-3 order-1 md:order-2 bg-[#B9FFCC] min-w-[147px] text-[#45654D] text-nav-d rounded-[8px]"
          >
            Accept invitation
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvitationModal;
